import { TContacts, TItem, TRowComponentProps } from '@libs/types';
import React from 'react';
import { TConsumerItem } from '../types/data';
import { RowEnum } from '../types/enums';
import { TTextOption } from '../types/options';

const FONT_VARIANTS_ORDER: NonNullable<TTextOption['variant']>[] = ['display1', 'display2', 'header', 'main', 'secondary'];

export const addScript = (filepath: string, attributes?: Record<string, string>, callback?: () => void): void => {
    if (filepath) {
        const fileRef = document.createElement('script');
        let done = false;
        const head = document.getElementsByTagName('head')[0];

        if (callback) {
            fileRef.onload = function () {
                if (!done) {
                    done = true;
                    callback();
                    if (head && fileRef.parentNode) {
                        head.removeChild(fileRef);
                    }
                }
            };
        }

        fileRef.setAttribute('type', 'text/javascript');
        fileRef.setAttribute('crossorigin', '');
        fileRef.setAttribute('src', filepath);

        if (attributes) {
            Object.entries(attributes).forEach(([key, value]) => {
                if (key && value) {
                    fileRef.setAttribute(key, value);
                }
            });
        }

        if (head) {
            head.appendChild(fileRef);
        }
    }
};

export const removeScript = (scriptPath: string): void => {
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i--) {
        const script = scripts[i];
        const src = script?.getAttribute('src');
        if (src && src.includes(scriptPath)) {
            script.parentNode?.removeChild(script);
        }
    }
};

export const convertItemToRowComponentProps = (item: TItem, id?: RowEnum): TRowComponentProps => {
    return {
        ...item,
        id: id ?? RowEnum.ROW_REGULAR,
        flagIcon: item.icon,
        isSelected: item.selected,
    };
};

export const convertContactsToRowComponentProps = (
    contacts: TContacts[],
    id: RowEnum.ROW_REGULAR | RowEnum.SELECTOR_REGULAR,
    ref: React.MutableRefObject<string | null>,
): TConsumerItem[] => {
    return contacts.map((contact) => {
        const blob = contact.icon[0];
        let avatar,
            iconName = 'ic_person';
        if (blob && blob instanceof Blob) {
            avatar = URL.createObjectURL(blob);
            ref.current = avatar;
            iconName = '';
        }
        return {
            id,
            title: contact.name[0],
            subtitle: contact.tel[0],
            iconVariant: 'circle',
            iconHierarchy: 'secondary',
            iconName,
            avatar,
        } as TConsumerItem;
    });
};

export const isMobileByTouch = () => {
    return 'ontouchstart' in window || navigator?.maxTouchPoints > 0;
};

export const getTextSizeVariant = (variant: TTextOption['variant'], maxLines: number, styles: Record<string, string>, element: HTMLElement) => {
    const measurementNode = element.cloneNode(true) as HTMLElement;
    Object.assign(measurementNode.style, {
        position: 'absolute',
        top: '0',
        visibility: 'hidden',
        pointerEvents: 'none',
        height: 'auto',
        //The clientWidth property returns the inner width of an element in pixels, including padding but excluding the border and scrollbar.
        // It also always rounds the value to the nearest integer.
        // getBoundingClientRect().width if you need a more precise measurement of the element's total width, including any fractional pixels
        width: `${element.getBoundingClientRect?.().width ?? element.clientWidth}px`,
        fontSize: '', // Ensure no inline font size interferes
    });
    document.body.appendChild(measurementNode);

    let newBestFitVariant = variant || FONT_VARIANTS_ORDER[0];
    const startIndex = FONT_VARIANTS_ORDER.indexOf(newBestFitVariant);

    if (startIndex !== -1) {
        // Iterate through variants from the one specified in props downwards.
        for (let i = startIndex; i < FONT_VARIANTS_ORDER.length; i++) {
            const testVariant = FONT_VARIANTS_ORDER[i];
            const prevVariantInLoop = i > startIndex ? FONT_VARIANTS_ORDER[i - 1] : newBestFitVariant;

            // Swap the variant class on our measurement node.
            if (styles[prevVariantInLoop]) {
                measurementNode.classList.remove(styles[prevVariantInLoop]);
            }
            if (styles[testVariant]) {
                measurementNode.classList.add(styles[testVariant]);
            }

            const style = window.getComputedStyle(measurementNode);
            const lineHeight = parseFloat(style.lineHeight);

            if (lineHeight > 0 && Math.round(measurementNode.scrollHeight / lineHeight) <= maxLines) {
                newBestFitVariant = testVariant;
                break;
            }
            newBestFitVariant = testVariant;
        }
    }
    document.body.removeChild(measurementNode);
    return newBestFitVariant;
};
